import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@/domain/entities/User';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { GetCurrentUserAction, LogoutAction } from '@/application/actions';
import { isTokenValid } from '@/shared/utils/tokenUtils';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  canAdminister: boolean;
  canOperateBilling: boolean;
  canManageUsers: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUserTenant: (tenant: { id: number | string; nombre: string; emailAdministrador?: string }) => void;
  applySession: (token: string, user: User) => void;
  mustChangePassword: boolean;
  updateUserEmail: (email: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const persistUser = (userToPersist: User) => {
    try {
      const tenant = userToPersist.getTenant();
      const userData = {
        id: userToPersist.id.getValue(),
        username: userToPersist.username,
        email: userToPersist.email.getValue(),
        role: userToPersist.role,
        tenant: tenant
          ? {
              id: tenant.id.getValue(),
              nombre: tenant.nombre,
              emailAdministrador: tenant.emailAdministrador.getValue(),
            }
          : null,
        createdAt: userToPersist.createdAt.toISOString(),
        lastLogin: userToPersist.lastLogin?.toISOString(),
        mustChangePassword: userToPersist.mustChangePassword,
      };
      localStorage.setItem('admin_user', JSON.stringify(userData));
    } catch (error) {
      console.error('Error saving user to localStorage:', error);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('admin_token');
      const storedUser = localStorage.getItem('admin_user');
      let fallbackUser: User | null = null;

      if (storedToken && storedUser) {
        // Validar formato y expiración del token antes de usarlo
        if (!isTokenValid(storedToken)) {
          // Token inválido o expirado, hacer logout
          logout();
          setIsLoading(false);
          return;
        }

        try {
          setToken(storedToken);
          
          // PRIMERO: Establecer usuario desde localStorage (sin esperar al backend)
          try {
            const userData = JSON.parse(storedUser);
            fallbackUser = User.create(
              userData.id,
              userData.username,
              userData.email,
              userData.role,
              userData.tenant || null,
              userData.createdAt ? new Date(userData.createdAt) : new Date(),
              userData.lastLogin ? new Date(userData.lastLogin) : undefined,
              Boolean(userData.mustChangePassword)
            );
            setUser(fallbackUser);
            setIsLoading(false); // Marcar como cargado inmediatamente
          } catch (parseError) {
            console.error('Error parsing stored user:', parseError);
            setIsLoading(false);
            return;
          }
          
          // SEGUNDO: Intentar obtener usuario del backend en segundo plano (opcional)
          // Si falla, ya tenemos el usuario del localStorage, así que no hacer logout
          setTimeout(async () => {
            try {
              const getCurrentUserAction = container.get<GetCurrentUserAction>(TYPES.GetCurrentUserAction);
              const currentUser = await getCurrentUserAction.execute();
              if (currentUser) {
                const localTenant = fallbackUser?.getTenant();
                if (localTenant) {
                  currentUser.changeTenant(localTenant);
                }
                setUser(currentUser);
                persistUser(currentUser);
              }
            } catch (error) {
              // Silenciar errores - usar el usuario del localStorage que ya establecimos
            }
          }, 500);
          
        } catch (error) {
          console.error('Error initializing auth:', error);
          // Solo hacer logout si realmente no hay datos guardados
          if (!storedToken || !storedUser) {
            logout();
          }
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const logout = () => {
    const logoutAction = container.get<LogoutAction>(TYPES.LogoutAction);
    logoutAction.execute();
    setUser(null);
    setToken(null);
  };

  const refreshUser = async () => {
    try {
      const getCurrentUserAction = container.get<GetCurrentUserAction>(TYPES.GetCurrentUserAction);
      const currentUser = await getCurrentUserAction.execute();
      if (currentUser) {
        const localTenant = user?.getTenant();
        if (localTenant) {
          currentUser.changeTenant(localTenant);
        }
        setUser(currentUser);
        persistUser(currentUser);
      }
      // Si no se puede obtener, mantener el usuario actual (no hacer logout)
    } catch (error: any) {
      console.warn('Error refreshing user:', error);
      // Solo hacer logout si es realmente un 401 (token inválido)
      // Para otros errores, mantener el usuario actual
      if (error?.response?.status === 401) {
        logout();
      }
    }
  };

  const updateUserTenant = (tenant: { id: number | string; nombre: string; emailAdministrador?: string }) => {
    if (!user) return;

    const updatedUser = User.create(
      user.id.getValue(),
      user.username,
      user.email.getValue(),
      user.role,
      {
        id: tenant.id,
        nombre: tenant.nombre,
        emailAdministrador: tenant.emailAdministrador || user.email.getValue(),
      },
      user.createdAt,
      user.lastLogin,
      user.mustChangePassword
    );

    setUser(updatedUser);
    persistUser(updatedUser);
  };

  const updateUserEmail = (email: string) => {
    if (!user) return;
    const tenant = user.getTenant();
    const updatedUser = User.create(
      user.id.getValue(),
      user.username,
      email,
      user.role,
      tenant
        ? {
            id: tenant.id.getValue(),
            nombre: tenant.nombre,
            emailAdministrador: tenant.emailAdministrador.getValue(),
          }
        : null,
      user.createdAt,
      user.lastLogin,
      user.mustChangePassword
    );
    setUser(updatedUser);
    persistUser(updatedUser);
  };

  const applySession = (nextToken: string, nextUser: User) => {
    localStorage.setItem('admin_token', nextToken);
    localStorage.setItem('authToken', nextToken);
    localStorage.setItem('token', nextToken);
    setToken(nextToken);
    setUser(nextUser);
    persistUser(nextUser);
  };

  const isAuthenticated = token !== null && user !== null;
  const canAdminister = user?.role === 'admin' || user?.role === 'manager';
  const canOperateBilling = canAdminister;
  const canManageUsers = user?.role === 'admin';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated,
        canAdminister,
        canOperateBilling,
        canManageUsers,
        setUser,
        setToken,
        logout,
        refreshUser,
        updateUserTenant,
        applySession,
        updateUserEmail,
        mustChangePassword: Boolean(user?.mustChangePassword),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
